import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Select,
  MenuItem,
  TextField
} from '@material-ui/core'
import { Users, DollarSign, Trophy } from 'lucide-react'
import { useStyles } from './style'
import clsx from 'clsx'
import SelectInput from '../SelectInput'
import { InputFieldText } from '../InputFieldText'

const PERIOD_LABELS = {
  today: 'Hoje',
  month: 'Mês atual',
  year: 'Ano atual',
  custom: 'Personalizado'
}

export function CompaniesMetrics({
  records = [],
  totalOpenValue = 0,
  totalOverdueValue = 0,
  period = 'month',
  onPeriodChange,
  customStartDate = '',
  onCustomStartDateChange,
  customEndDate = '',
  onCustomEndDateChange
}) {
  console.log('Dados', records)
  const classes = useStyles()

  const formatCurrency = value => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'R$ 0,00'
    }
    return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  const metrics = useMemo(() => {
    const total = records.length
    const active = records.filter(r => r.status !== false).length
    const inactive = total - active

    // Agrupamento e contagem de planos mais ativos
    const planCounts = records.reduce((acc, current) => {
      const planName = current.plan?.name || current.planId || 'Sem Plano'
      acc[planName] = (acc[planName] || 0) + 1
      return acc
    }, {})

    const sortedPlans = Object.entries(planCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)

    return { total, active, inactive, sortedPlans }
  }, [records])

  return (
    <Grid
      container
      component="section"
      aria-label="Métricas das Empresas"
      className={classes.containerMetrics}
    >
      {/* Card 1: Empresas */}
      <Grid item xs={12} md={4}>
        <Paper elevation={0} variant="outlined" className={classes.boxMetrics}>
          <Box display="flex" alignItems="center" mb={3}>
            <Box className={clsx(classes.boxIconBase, classes.boxCompany)}>
              <Users size={20} aria-hidden="true" />
            </Box>
            <Typography variant="subtitle1" className={classes.titleCompany}>
              Empresas
            </Typography>
          </Box>
          <Box className={classes.boxContentResults}>
            <Box className={clsx(classes.boxBase, classes.boxTotal)}>
              <Typography variant="h5" className={classes.titleTotal}>
                {metrics.total}
              </Typography>
              <Typography variant="caption" className={classes.subtitleTotal}>
                Total
              </Typography>
            </Box>
            <Box className={clsx(classes.boxBase, classes.boxActive)}>
              <Typography variant="h5" className={classes.titleActive}>
                {metrics.active}
              </Typography>
              <Typography variant="caption" className={classes.subtitleActive}>
                Ativas
              </Typography>
            </Box>
            <Box className={clsx(classes.boxBase, classes.boxInactive)}>
              <Typography
                variant="h5"
                style={{ fontWeight: 700, color: '#dc2626' }}
              >
                {metrics.inactive}
              </Typography>
              <Typography
                variant="caption"
                style={{ color: '#dc2626', fontWeight: 500 }}
              >
                Inativas
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Card 2: Financeiro */}
      <Grid item xs={12} md={4}>
        <Paper elevation={0} variant="outlined" className={classes.boxMetrics}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Box display="flex" alignItems="center">
              <Box className={clsx(classes.boxIconBase, classes.boxFinancy)}>
                <DollarSign size={20} aria-hidden="true" />
              </Box>
              <Typography variant="subtitle1" className={classes.titleFinancy}>
                Financeiro
              </Typography>
            </Box>
            <Grid item xs={5}>
              <SelectInput
                value={period}
                onChange={onPeriodChange}
                variant="outlined"
                margin="dense"
                style={{ fontSize: '0.75rem', minWidth: 130 }}
                renderValue={value => PERIOD_LABELS[value] || value}
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                  transformOrigin: { vertical: 'top', horizontal: 'right' }
                }}
              >
                <MenuItem value="today">{PERIOD_LABELS.today}</MenuItem>
                <MenuItem value="month">{PERIOD_LABELS.month}</MenuItem>
                <MenuItem value="year">{PERIOD_LABELS.year}</MenuItem>
                <MenuItem value="custom">{PERIOD_LABELS.custom}</MenuItem>
              </SelectInput>
            </Grid>
          </Box>
          {period === 'custom' && (
            <Box className={classes.boxCustomDateRange}>
              <InputFieldText
                type="date"
                label="Início"
                value={customStartDate}
                onChange={onCustomStartDateChange}
                variant="outlined"
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <InputFieldText
                type="date"
                label="Fim"
                value={customEndDate}
                onChange={onCustomEndDateChange}
                variant="outlined"
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
          <Box className={classes.boxContentResults}>
            <Box className={clsx(classes.boxBaseFinancy, classes.boxActive)}>
              <Typography
                variant="caption"
                className={classes.subtitleFinancyActive}
              >
                Ativas
              </Typography>
              <Typography variant="h6" className={classes.titleFinancyActive}>
                {formatCurrency(totalOpenValue)}
              </Typography>
            </Box>
            <Box className={clsx(classes.boxBaseFinancy, classes.boxInactive)}>
              <Typography
                variant="caption"
                className={classes.subtitleFinancyLate}
              >
                Em atraso
              </Typography>
              <Typography variant="h6" className={classes.titleFinancyLate}>
                {formatCurrency(totalOverdueValue)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Card 3: Planos Mais Ativos */}
      <Grid item xs={12} md={4}>
        <Paper elevation={0} variant="outlined" className={classes.boxMetrics}>
          <Box display="flex" alignItems="center" mb={2}>
            <Box className={clsx(classes.boxIconBase, classes.boxPlans)}>
              <Trophy size={20} aria-hidden="true" />
            </Box>
            <Typography variant="subtitle1" className={classes.titlePlans}>
              Planos Mais Ativos
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
            {metrics.sortedPlans.map((plan, index) => {
              const bgColors = ['#fef3c7', '#f1f5f9', '#ffedd5']
              const textColors = ['#d97706', '#475569', '#ea580c']
              return (
                <Box className={classes.cardPlans} key={plan.name}>
                  <Box display="flex" alignItems="center">
                    <Box
                      className={classes.chipPlans}
                      style={{
                        backgroundColor: bgColors[index] || '#f1f5f9',
                        color: textColors[index] || '#475569'
                      }}
                    >
                      {index + 1}º
                    </Box>
                    <Typography variant="body2" className={classes.planName}>
                      {plan.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" style={{ color: '#64748b' }}>
                    {plan.count} {plan.count === 1 ? 'empresa' : 'empresas'}
                  </Typography>
                </Box>
              )
            })}
            {metrics.sortedPlans.length === 0 && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ textAlign: 'center', padding: 8 }}
              >
                Nenhum plano ativo encontrado.
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  )
}

CompaniesMetrics.propTypes = {
  records: PropTypes.array,
  totalOpenValue: PropTypes.number,
  totalOverdueValue: PropTypes.number,
  period: PropTypes.string,
  onPeriodChange: PropTypes.func,
  customStartDate: PropTypes.string,
  onCustomStartDateChange: PropTypes.func,
  customEndDate: PropTypes.string,
  onCustomEndDateChange: PropTypes.func
}
